import { motion } from "framer-motion";
import { Instagram, ExternalLink, ArrowUpRight } from "lucide-react";
import { BlurIn, ScaleReveal } from "./ScrollAnimations";

const INSTAGRAM_URL = "https://www.instagram.com/newyeardiaries/";

export const InstagramSection = () => {
  return (
    <section id="instagram" data-testid="instagram-section" className="relative overflow-hidden bg-[#1A1A1A]">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#C8A23A]/30 to-transparent" aria-hidden="true" />
      <div className="absolute top-20 -left-32 w-64 h-64 rounded-full bg-[#7A1A31]/10 blur-3xl" aria-hidden="true" />
      <div className="absolute bottom-20 -right-32 w-72 h-72 rounded-full bg-[#C8A23A]/8 blur-3xl" aria-hidden="true" />

      <div className="container mx-auto px-6 sm:px-8 max-w-7xl py-24 sm:py-32">
        {/* Header row */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 mb-14">
          <BlurIn>
            <div className="flex items-center gap-5 mb-5">
              <motion.div
                className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] flex items-center justify-center shadow-lg shadow-[#DD2A7B]/20"
                whileHover={{ rotate: 8, scale: 1.08 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Instagram className="w-6 h-6 text-white" />
              </motion.div>
              <div>
                <span
                  className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C8A23A] block mb-1"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  Follow Our Journey
                </span>
                <h2
                  className="text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                  data-testid="instagram-heading"
                >
                  @newyeardiaries
                </h2>
              </div>
            </div>
            <p
              className="text-base text-white/50 max-w-md leading-relaxed"
              style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
            >
              Sneak peeks, new collections, behind-the-scenes craftsmanship, and corporate gifting inspiration.
            </p>
          </BlurIn>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <motion.a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-follow-link"
              className="group inline-flex items-center gap-3 bg-white/[0.06] backdrop-blur-sm border border-white/10 text-white px-8 py-4 rounded-full hover:bg-white hover:text-[#1A1A1A] transition-all duration-400 text-sm font-medium"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              Follow Us on Instagram
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </motion.a>
          </motion.div>
        </div>

        {/* Instagram embed - premium framed */}
        <ScaleReveal>
          <div className="relative">
            {/* Glow behind the frame */}
            <div className="absolute -inset-1 bg-gradient-to-br from-[#C8A23A]/20 via-transparent to-[#7A1A31]/20 rounded-2xl blur-sm" aria-hidden="true" />
            <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-white shadow-2xl shadow-black/40">
              <iframe
                src="https://www.instagram.com/newyeardiaries/embed"
                title="Eagle Diaries Instagram Feed"
                data-testid="instagram-embed"
                className="w-full border-0 bg-white"
                style={{ minHeight: "560px" }}
                loading="lazy"
                allowTransparency="true"
              />
            </div>
          </div>
        </ScaleReveal>
      </div>

      {/* Bottom gradient line */}
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#C8A23A]/30 to-transparent" aria-hidden="true" />
    </section>
  );
};
